import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination, Keyboard, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { api, uploadUrl } from '../api/client.js';

const FALLBACK_BG = [
  'https://picsum.photos/seed/hero1/1600/900',
  'https://picsum.photos/seed/hero2/1600/900',
  'https://picsum.photos/seed/hero3/1600/900',
];

export default function Hero() {
  const [slides, setSlides] = useState(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    api.get('/carousel').then(({ data }) => {
      setSlides(data.slides?.length ? data.slides : [{
        title: 'Jasa Edukasi Satwa & School Safari Interaktif',
        subtitle: 'Pengalaman belajar langsung bersama hewan eksotis untuk sekolah dan acara di Indonesia',
        button_text: 'PESAN SEKARANG',
        button_url: '/kontak',
        image: '',
      }]);
    }).catch(() => setSlides([]));
  }, []);

  if (!slides) {
    return <section id="hero" className="h-screen min-h-[600px] flex items-center justify-center bg-neutral-900" />;
  }

  const multi = slides.length > 1;

  return (
    <section id="hero" className="hero-swiper relative overflow-hidden" style={{ height: '100dvh', minHeight: 600 }}>
      <Swiper
        modules={[Autoplay, Navigation, Pagination, Keyboard, A11y]}
        className="h-full w-full"
        loop={multi}
        speed={700}
        autoplay={multi ? { delay: 5000, disableOnInteraction: false } : false}
        pagination={multi ? { clickable: true } : false}
        keyboard={{ enabled: true }}
        a11y={{ enabled: true }}
        onBeforeInit={(swiper) => {
          if (typeof swiper.params.navigation !== 'boolean') {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
          }
        }}
        navigation={multi ? { prevEl: prevRef.current, nextEl: nextRef.current } : false}
      >
        {slides.map((slide, i) => {
          const bg = slide.image ? uploadUrl(slide.image) : FALLBACK_BG[i % FALLBACK_BG.length];
          const isBtnInternal = (slide.button_url || '').startsWith('#') || (slide.button_url || '').startsWith('/');
          return (
            <SwiperSlide key={i} className="relative h-full w-full">
              <div className="hero-bg absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${bg}')` }} />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(10,30,10,0.75) 0%, rgba(20,50,20,0.6) 50%, rgba(10,20,10,0.8) 100%)' }} />
              <div className="relative h-full flex items-center justify-center text-center px-4">
                <div className="max-w-4xl mx-auto">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold text-white mb-6 border border-white/20" style={{ background: 'color-mix(in oklab, var(--accent) 40%, transparent)', backdropFilter: 'blur(8px)' }}>
                    <span className="material-symbols-outlined text-yellow-400">pets</span>
                    <span>Exotic Pet World</span>
                  </div>
                  <h1 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight mb-6 drop-shadow-2xl">{slide.title}</h1>
                  <p className="text-lg md:text-xl text-white/85 mb-10 max-w-2xl mx-auto leading-relaxed">{slide.subtitle}</p>
                  {isBtnInternal && !slide.button_url?.startsWith('#') ? (
                    <Link to={slide.button_url || '/kontak'} className="inline-flex items-center gap-3 px-8 py-4 rounded-3xl font-black text-neutral-900 text-base shadow-2xl bg-gradient-to-br from-amber-400 to-yellow-300 hover:shadow-yellow-400/30 hover:scale-105 hover:rounded-2xl active:rounded-3xl active:scale-95 transition-all duration-300">
                      <span className="material-symbols-outlined">event</span>
                      {slide.button_text || 'CEK JADWAL EVENT'}
                    </Link>
                  ) : (
                    <a href={slide.button_url || '/kontak'} className="inline-flex items-center gap-3 px-8 py-4 rounded-3xl font-black text-neutral-900 text-base shadow-2xl bg-gradient-to-br from-amber-400 to-yellow-300 hover:shadow-yellow-400/30 hover:scale-105 hover:rounded-2xl active:rounded-3xl active:scale-95 transition-all duration-300">
                      <span className="material-symbols-outlined">event</span>
                      {slide.button_text || 'CEK JADWAL EVENT'}
                    </a>
                  )}
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>

      {multi && (
        <>
          <button ref={prevRef} className="hero-nav-btn hidden md:flex absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center text-white border border-white/30 z-10">
            <span className="material-symbols-outlined text-xl">chevron_left</span>
          </button>
          <button ref={nextRef} className="hero-nav-btn hidden md:flex absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center text-white border border-white/30 z-10">
            <span className="material-symbols-outlined text-xl">chevron_right</span>
          </button>
        </>
      )}

      {/* Statis, di luar Swiper — tidak ikut animasi transisi slide */}
      <div className="hidden md:flex absolute bottom-8 right-8 md:right-12 flex-col items-center gap-2 text-white/60 text-xs font-medium z-10 pointer-events-none">
        <span style={{ writingMode: 'vertical-rl' }} className="tracking-widest">SCROLL DOWN</span>
        <div className="w-px h-12 bg-white/30 relative overflow-hidden">
          <div className="w-full bg-yellow-400 scroll-indicator" />
        </div>
      </div>
    </section>
  );
}
